/**
 * Maps backend enum values onto the presentation tokens declared in index.css.
 *
 * Kept in one file so a status never looks different on two screens, and so
 * adding a status is a single edit rather than a hunt through components.
 */

export type Tone =
  | "draft"
  | "pending"
  | "progress"
  | "review"
  | "approved"
  | "rejected"
  | "overdue"
  | "hold";

const REQUEST_TONES: Record<string, Tone> = {
  draft: "draft",
  submitted: "pending",
  under_review: "review",
  accepted: "progress",
  rejected: "rejected",
  samples_received: "progress",
  in_testing: "progress",
  testing_complete: "review",
  awaiting_certification: "review",
  certified: "approved",
  closed: "approved",
  cancelled: "draft",
  on_hold: "hold",
};

const TEST_TONES: Record<string, Tone> = {
  pending: "draft",
  assigned: "pending",
  in_progress: "progress",
  results_entered: "progress",
  awaiting_review: "review",
  reviewed: "review",
  approved: "approved",
  rejected: "rejected",
  repeat_required: "rejected",
  cancelled: "draft",
  on_hold: "hold",
};

const SAMPLE_TONES: Record<string, Tone> = {
  expected: "pending",
  received: "progress",
  accepted: "progress",
  rejected: "rejected",
  in_storage: "progress",
  in_testing: "progress",
  tested: "approved",
  retained: "review",
  disposed: "draft",
  returned: "draft",
};

const CERTIFICATE_TONES: Record<string, Tone> = {
  draft: "draft",
  pending_approval: "review",
  approved: "progress",
  issued: "approved",
  amended: "review",
  superseded: "draft",
  revoked: "rejected",
};

const EQUIPMENT_TONES: Record<string, Tone> = {
  in_service: "approved",
  idle: "draft",
  under_maintenance: "hold",
  awaiting_calibration: "pending",
  calibration_overdue: "overdue",
  out_of_service: "rejected",
  retired: "draft",
};

// A lot on the shelf. `quarantine` is deliberately "pending" rather than a
// warning colour: a delivery waiting on its incoming check is the system working,
// not something going wrong. `expired` is `overdue` because it is the same kind of
// fact as a lapsed calibration — stock that is physically present and unusable.
const LOT_TONES: Record<string, Tone> = {
  quarantine: "pending",
  available: "approved",
  in_use: "progress",
  reserved: "review",
  exhausted: "draft",
  expired: "overdue",
  rejected: "rejected",
  recalled: "rejected",
  disposed: "draft",
};

// A revision of a controlled document. `approved` is `progress` rather than
// `approved`: it has been signed but is not yet the copy anybody works to, and
// colouring it as done is how a bench ends up using next month's method today.
const DOCUMENT_TONES: Record<string, Tone> = {
  draft: "draft",
  in_review: "review",
  approved: "progress",
  effective: "approved",
  superseded: "draft",
  withdrawn: "rejected",
};

// A validation study. `rejected` is the study that concluded the method is *not*
// fit for purpose — a legitimate, useful result, but one that colours like a
// failure because its consequence is that the determination is not offered.
const VALIDATION_TONES: Record<string, Tone> = {
  planned: "draft",
  in_progress: "progress",
  in_review: "review",
  approved: "approved",
  rejected: "rejected",
  superseded: "draft",
  withdrawn: "rejected",
};

const WORKFLOW_TONES: Record<string, Tone> = {
  draft: "draft",
  in_progress: "progress",
  completed: "approved",
  rejected: "rejected",
  cancelled: "draft",
  on_hold: "hold",
};

// A disposal run. `approved` is `progress` for the reason a document revision's is:
// signed off, but nothing has left the shelf yet.
const DISPOSAL_TONES: Record<string, Tone> = {
  draft: "draft",
  submitted: "review",
  approved: "progress",
  completed: "approved",
  cancelled: "draft",
};

// A monitoring log entry. Sealed is the analyst's signature; countersigned is the second.
const MONITORING_TONES: Record<string, Tone> = {
  pending: "pending",
  draft: "progress",
  submitted: "review",
  countersigned: "approved",
};

// A database backup or restore. `running` is `progress` rather than `pending`: nobody
// is being waited on, the platform is working, and it should look like work.
const BACKUP_TONES: Record<string, Tone> = {
  running: "progress",
  completed: "approved",
  failed: "rejected",
};

export type Domain =
  | "request"
  | "test"
  | "sample"
  | "certificate"
  | "equipment"
  | "lot"
  | "document"
  | "validation"
  | "workflow"
  | "disposal"
  | "monitoring"
  | "backup";

const MAPS: Record<Domain, Record<string, Tone>> = {
  request: REQUEST_TONES,
  test: TEST_TONES,
  sample: SAMPLE_TONES,
  certificate: CERTIFICATE_TONES,
  equipment: EQUIPMENT_TONES,
  lot: LOT_TONES,
  document: DOCUMENT_TONES,
  validation: VALIDATION_TONES,
  workflow: WORKFLOW_TONES,
  disposal: DISPOSAL_TONES,
  monitoring: MONITORING_TONES,
  backup: BACKUP_TONES,
};

export function toneFor(domain: Domain, status: string | null | undefined): Tone {
  if (!status) return "draft";
  return MAPS[domain][status] ?? "draft";
}

/** Tailwind classes for a tone, resolved from the CSS custom properties. */
export function toneClasses(tone: Tone): string {
  switch (tone) {
    case "approved":
      return "text-status-approved bg-status-approved-bg";
    case "rejected":
      return "text-status-rejected bg-status-rejected-bg";
    case "overdue":
      return "text-status-overdue bg-status-overdue-bg";
    case "progress":
      return "text-status-progress bg-status-progress-bg";
    case "review":
      return "text-status-review bg-status-review-bg";
    case "pending":
      return "text-status-pending bg-status-pending-bg";
    case "hold":
      return "text-status-hold bg-status-hold-bg";
    default:
      return "text-status-draft bg-status-draft-bg";
  }
}

export type VerdictClass = "verdict-pass" | "verdict-fail" | "verdict-warn" | "verdict-neutral";

export function verdictClass(evaluation: string | null | undefined): VerdictClass {
  switch (evaluation) {
    case "conforms":
      return "verdict-pass";
    case "not_conforms":
      return "verdict-fail";
    case "inconclusive":
      return "verdict-warn";
    default:
      return "verdict-neutral";
  }
}

/** Accent colour for a department, taken from the seeded organogram colours. */
export function departmentAccent(code: string | null | undefined): string {
  if (!code) return "var(--muted-foreground)";
  if (code.includes("MICRO")) return "var(--dept-microbiology)";
  if (code.includes("PHYS")) return "var(--dept-physchem)";
  if (code.startsWith("TOX")) return "var(--dept-toxicology)";
  if (code.startsWith("BIO")) return "var(--dept-biologicals)";
  if (code.startsWith("QA")) return "var(--dept-quality)";
  if (code.startsWith("SMP")) return "var(--dept-sample)";
  if (code.startsWith("EQP")) return "var(--dept-equipment)";
  return "var(--brand-blue)";
}
